/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { EnumExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button, Popover } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { IInvoiceStatusResources } from '../helpers/invoice-status';

export interface IInvoicesFilterResources extends IInvoiceStatusResources {
    invoicesFilterShowAll: string;
}

export interface IInvoicesFilterProps extends IComponentProps {
    className: string;
    disabled: boolean;

    resources: IInvoicesFilterResources;

    currentState: InvoicesFilterState;
    onFilterStateChanged(state: InvoicesFilterState): void;
}

export enum InvoicesFilterState {
    ShowAll,
    Paid,
    PartiallyPaid,
    Unpaid
}

const getInvoicesFilterStateResources = (filterState: InvoicesFilterState, resources: IInvoicesFilterResources): string => {
    switch (filterState) {
        case InvoicesFilterState.ShowAll:
            return resources.invoicesFilterShowAll;
        case InvoicesFilterState.Paid:
            return resources.invoiceStatusPaid;
        case InvoicesFilterState.PartiallyPaid:
            return resources.invoiceStatusPartiallyPaid;
        case InvoicesFilterState.Unpaid:
            return resources.invoiceStatusUnpaid;
        default:
            throw new Error('Failed to retrieve resource description for unknown invoices filter state.');
    }
};

/**
 * On Click function.
 * @param onStateClick -Invoice filter state function.
 * @param filterState -Invoice filter state.
 * @returns Set Invoice filter state.
 */
const onClickHandler = (onStateClick: (filterState: InvoicesFilterState) => void, filterState: InvoicesFilterState) => () => {
    onStateClick(filterState);
};

const renderFilterStateItem = (
    filterState: InvoicesFilterState,
    resources: IInvoicesFilterResources,
    className: string,
    onStateClick: (filterState: InvoicesFilterState) => void
): React.ReactNode => {
    const stateStringRepresentation = InvoicesFilterState[filterState];

    return (
        <button className={`${className}__item__${stateStringRepresentation}`} onClick={onClickHandler(onStateClick, filterState)}>
            {getInvoicesFilterStateResources(filterState, resources)}
        </button>
    );
};

const InvoicesFilter: React.FC<IInvoicesFilterProps> = (props: IInvoicesFilterProps) => {
    const popoverRef = React.createRef<HTMLButtonElement>();
    const [popoverState, setPopoverState] = React.useState(false);
    const togglePopover = React.useCallback(() => {
        setPopoverState(!popoverState);
    }, [popoverState]);

    const [filterState, setFilterState] = React.useState(props.currentState);

    const onFilterStateClick = (clickedState: InvoicesFilterState) => {
        setFilterState(clickedState);
        setPopoverState(false);
        props.onFilterStateChanged(clickedState);
    };

    return (
        <div className={props.className}>
            <Button
                className={`${props.className}__expand-filter-button`}
                innerRef={popoverRef}
                aria-expanded={popoverState}
                aria-describedby={props.className}
                onClick={togglePopover}
                disabled={props.disabled}
            >
                {getInvoicesFilterStateResources(filterState, props.resources)}
            </Button>
            <Popover id={props.className} placement='bottom-end' isOpen={popoverState} target={popoverRef} toggle={togglePopover}>
                {EnumExtensions.getEnumValues<InvoicesFilterState>(InvoicesFilterState).map(state => {
                    return renderFilterStateItem(state, props.resources, props.className, onFilterStateClick);
                })}
            </Popover>
        </div>
    );
};

export const InvoicesFilterComponent: React.FunctionComponent<IInvoicesFilterProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IInvoicesFilterProps
>('InvoicesFilterComponent', { component: InvoicesFilter });
